import { useRef, useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { selectDealerSelected } from '../../../redux/headerSlice/selectors';
import { logger } from 'app/utils/logger';
import { ChatPanelProps } from '../../../types';

export interface UseCleanChatPanelReturn {
  messagesEndRef: React.RefObject<HTMLDivElement>;
  copiedMessageId: number | null;
  previewOpen: boolean;
  previewTemplateId: string | undefined;
  currentDealerId: string;
  handleSendMessage: () => void;
  handleKeyPress: (e: React.KeyboardEvent) => void;
  handleCopyMessage: (text: string, messageIndex: number) => void;
  handlePreviewClick: (e: React.MouseEvent, templateId: string) => void;
  handleClosePreview: () => void;
  handleInputChange: (value: string) => void;
}

export const useCleanChatPanel = ({
  inputText,
  setInputText,
  onSendMessage,
  loading = false,
  isInputRestricted = false,
  chatHistory,
}: Pick<ChatPanelProps, 'inputText' | 'setInputText' | 'onSendMessage' | 'loading' | 'isInputRestricted' | 'chatHistory'>): UseCleanChatPanelReturn => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [copiedMessageId, setCopiedMessageId] = useState<number | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTemplateId, setPreviewTemplateId] = useState<string | undefined>(undefined);

  // Get selected dealer
  const selectedDealer = useSelector(selectDealerSelected);
  const currentDealerId = selectedDealer?.dealer || '';

  // Auto scroll to bottom - memoized
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [chatHistory, loading, scrollToBottom]);

  const handleSendMessage = useCallback(() => {
    if (inputText.trim() && !loading && !isInputRestricted) {
      onSendMessage(inputText.trim());
    }
  }, [inputText, loading, isInputRestricted, onSendMessage]);

  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !isInputRestricted) {
      e.preventDefault();
      handleSendMessage();
    }
  }, [isInputRestricted, handleSendMessage]);

  const handleCopyMessage = useCallback((text: string, messageIndex: number) => {
    try {
      // For MarkupMAIQ content, we need to extract plain text
      const plainText = text.replace(/<[^>]*>/g, '').replace(/\*\*(.*?)\*\*/g, '$1').replace(/\*(.*?)\*/g, '$1');
      navigator.clipboard.writeText(plainText).then(() => {
        setCopiedMessageId(messageIndex);
        setTimeout(() => {
          setCopiedMessageId(null);
        }, 3000);
      }).catch((err) => {
        logger.error('Failed to copy message to clipboard', err);
      });
    } catch (error) {
      logger.error('Error copying message', error);
    }
  }, []);

  const handlePreviewClick = useCallback((e: React.MouseEvent, templateId: string) => {
    e.stopPropagation(); // Prevent triggering the button click
    
    if (!currentDealerId) {
      logger.warn('Dealer ID is required for preview');
      return;
    }

    try {
      logger.info('Opening email template preview', { dealerId: currentDealerId, templateId });
      setPreviewTemplateId(templateId);
      setPreviewOpen(true);
    } catch (error) {
      logger.error('Error opening preview', error);
    }
  }, [currentDealerId]);

  const handleClosePreview = useCallback(() => {
    try {
      setPreviewOpen(false);
      setPreviewTemplateId(undefined);
    } catch (error) {
      logger.error('Error closing preview', error);
    }
  }, []);

  const handleInputChange = useCallback((value: string) => {
    try {
      setInputText(value);
    } catch (error) {
      logger.error('Error updating input text', error);
    }
  }, [setInputText]);

  return {
    messagesEndRef,
    copiedMessageId,
    previewOpen,
    previewTemplateId,
    currentDealerId,
    handleSendMessage,
    handleKeyPress,
    handleCopyMessage,
    handlePreviewClick,
    handleClosePreview,
    handleInputChange,
  };
};

